<template><div><p class="d-docsite--paragraph">The <code v-pre>font-variant-numeric</code> CSS property controls the usage of alternate glyphs for numbers, fractions, and ordinal markers.</p>
<h2 id="normal-default" tabindex="-1" class="d-docsite--header-2">Normal (Default) <a class="header-anchor d-link d-docsite--link d-link" href="#normal-default" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-fvn-normal</code> to deactivate any alternate glyphs.</p>
<code-well-header>
  <p class="d-fvn-normal">1234567890</p>
</code-well-header>
<div class="language-html" data-highlighter="prismjs" data-ext="html"><pre v-pre><code class="language-html"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>p</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-fvn-normal<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>...<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>p</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div><h2 id="ordinal" tabindex="-1" class="d-docsite--header-2">Ordinal <a class="header-anchor d-link d-docsite--link d-link" href="#ordinal" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">The class <code v-pre>d-fvn-ordinal</code> forces the use of special glyphs for ordinal markers, like 1st, 2nd, 3rd.</p>
<code-well-header>
  <p class="d-fvn-ordinal">1st, 2nd, 3rd, 4th</p>
</code-well-header>
<div class="language-html" data-highlighter="prismjs" data-ext="html"><pre v-pre><code class="language-html"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>p</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-fvn-ordinal<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>1st, 2nd, 3rd, 4th<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>p</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div><h2 id="proportional-nums" tabindex="-1" class="d-docsite--header-2">Proportional-Nums <a class="header-anchor d-link d-docsite--link d-link" href="#proportional-nums" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-fvn-proportional</code> to set different sizes for each number.</p>
<code-well-header>
  <div class="d-fvn-proportional">
    <table class="numbers-table">
      <tbody>
      <tr>
        <td><span>21.0</span></td>
        <td><span>160</span></td>
        <td><span>3.90</span></td>
        <td><span>2.875</span></td>
        <td><span>17.02</span></td>
      </tr>
      <tr>
        <td><span>22.8</span></td>
        <td><span>108</span></td>
        <td><span>3.85</span></td>
        <td><span>2.320</span></td>
        <td><span>18.61</span></td>
      </tr>
      <tr>
        <td><span>21.4</span></td>
        <td><span>258</span></td>
        <td><span>3.08</span></td>
        <td><span>3.215</span></td>
        <td><span>19.44</span></td>
      </tr>
    </tbody></table>
  </div>
</code-well-header>
<div class="language-html" data-highlighter="prismjs" data-ext="html"><pre v-pre><code class="language-html"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>div</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-fvn-proportional<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>...<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>div</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div><h2 id="tabular-nums" tabindex="-1" class="d-docsite--header-2">Tabular-Nums <a class="header-anchor d-link d-docsite--link d-link" href="#tabular-nums" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-fvn-tabular</code> for aligning tabular data and preventing layout shifts of dynamically-changing content. For example, a timer counting down.</p>
<code-well-header>
  <div class="d-fvn-tabular">
    <table class="numbers-table">
      <tbody>
      <tr>
        <td><span>21.0</span></td>
        <td><span>160</span></td>
        <td><span>3.90</span></td>
        <td><span>2.875</span></td>
        <td><span>17.02</span></td>
      </tr>
      <tr>
        <td><span>22.8</span></td>
        <td><span>108</span></td>
        <td><span>3.85</span></td>
        <td><span>2.320</span></td>
        <td><span>18.61</span></td>
      </tr>
      <tr>
        <td><span>21.4</span></td>
        <td><span>258</span></td>
        <td><span>3.08</span></td>
        <td><span>3.215</span></td>
        <td><span>19.44</span></td>
      </tr>
    </tbody></table>
  </div>
</code-well-header>
<div class="language-html" data-highlighter="prismjs" data-ext="html"><pre v-pre><code class="language-html"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>div</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-fvn-tabular<span class="token punctuation">"</span></span><span class="token punctuation">></span></span><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>table</span><span class="token punctuation">></span></span>...<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>table</span><span class="token punctuation">></span></span><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>div</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div><h2 id="diagonal-fractions" tabindex="-1" class="d-docsite--header-2">Diagonal-Fractions <a class="header-anchor d-link d-docsite--link d-link" href="#diagonal-fractions" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-fvn-diagonal</code> to display numerator and denominator smaller and separated by a slash.</p>
<code-well-header>
  <p class="d-fvn-diagonal">1/2 3/4 5/6</p>
</code-well-header>
<div class="language-html" data-highlighter="prismjs" data-ext="html"><pre v-pre><code class="language-html"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>p</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-fvn-diagonal<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>1/2 3/4 5/6<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>p</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div><h2 id="unset" tabindex="-1" class="d-docsite--header-2">Unset <a class="header-anchor d-link d-docsite--link d-link" href="#unset" aria-hidden="true">#</a></h2>
<p class="d-docsite--paragraph">Use <code v-pre>d-fvn-unset</code> to deactivate other value previously set to <code v-pre>font-variant-numeric</code>.</p>
<code-well-header>
  <p class="d-fvn-unset">1234567890</p>
</code-well-header>
<div class="language-html" data-highlighter="prismjs" data-ext="html"><pre v-pre><code class="language-html"><span class="line"><span class="token tag"><span class="token tag"><span class="token punctuation">&lt;</span>p</span> <span class="token attr-name">class</span><span class="token attr-value"><span class="token punctuation attr-equals">=</span><span class="token punctuation">"</span>d-fvn-unset<span class="token punctuation">"</span></span><span class="token punctuation">></span></span>...<span class="token tag"><span class="token tag"><span class="token punctuation">&lt;/</span>p</span><span class="token punctuation">></span></span></span>
<span class="line"></span></code></pre>
</div><h2 id="classes" tabindex="-1" class="d-docsite--header-2">Classes <a class="header-anchor d-link d-docsite--link d-link" href="#classes" aria-hidden="true">#</a></h2>
<utility-class-table>
  <template #content>
    <tbody>
      <tr v-for="item in fontVariantNumeric">
        <th scope="row" class="d-code--sm d-docsite-code">.d-fvn-{{ item.class }}</th>
        <td class="d-code--sm">font-variant-numeric: {{ item.output }} !important;</td>
      </tr>
    </tbody>
  </template>
</utility-class-table>
</div></template>


<script setup>
  import { fontVariantNumeric } from '@data/type.json';
</script>
<style lang="less" scoped>
  .numbers-table {
    border-collapse: collapse;
  }
  .numbers-table td {
    border: 1px solid var(--dt-color-border-moderate);
  }
</style>